import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  ApiError,
  setupMailboxPrincipal,
  updateMailRouteDraftMailboxProfile,
  type MailRouteDraft
} from './api';
import {
  buildMailboxProfileInput,
  canSwitchMailbox,
  draftIsStale,
  mailboxFormValue,
  mailboxOptionLabel,
  mailboxesForDomain,
  type MailboxFormValue,
  type MailboxProfileInput
} from './mailbox-editor-helpers';
import './MailboxEditor.css';

export const NEW_MAILBOX_VALUE = '';

type MailboxEditorProps = {
  domain: string;
  draft: MailRouteDraft;
  onDraftChange: (draft: MailRouteDraft) => boolean;
};

type SaveStatus = 'idle' | 'saving' | 'success' | 'warning' | 'error';

export function MailboxEditor({ domain, draft, onDraftChange }: MailboxEditorProps) {
  const alternativeSequence = useRef(0);
  const [selected, setSelected] = useState(NEW_MAILBOX_VALUE);
  const [form, setForm] = useState<MailboxFormValue>(() => withFreshRows(mailboxFormValue(draft, domain, null)));
  const [baseRevision, setBaseRevision] = useState(draft.revision);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [message, setMessage] = useState('');
  const [retrySetup, setRetrySetup] = useState(false);

  const mailboxes = mailboxesForDomain(draft, domain);
  const selectedMailboxId = selected === NEW_MAILBOX_VALUE ? null : selected;
  const selectedMailbox = selectedMailboxId
    ? mailboxes.find((mailbox) => mailbox.id === selectedMailboxId) ?? null
    : null;
  const creating = selectedMailboxId === null;
  const missingPrimary = !creating && !form.primaryAddress;
  const primaryDomain = form.primaryAddress.trim().toLowerCase().split('@')[1] ?? '';
  const primaryDomainMismatch = !creating && Boolean(primaryDomain) && primaryDomain !== domain.trim().toLowerCase();
  const stale = draftIsStale(baseRevision, draft.revision);
  const switchAllowed = canSwitchMailbox(dirty, busy) && !retrySetup;
  const formDisabled = busy || retrySetup;
  const saveDisabled = busy || stale || (!dirty && !retrySetup) || (!creating && (!selectedMailbox || missingPrimary || primaryDomainMismatch));

  useEffect(() => {
    if (draft.revision === baseRevision) return;

    if (dirty || busy || retrySetup) return;

    const retainedSelection = selected !== NEW_MAILBOX_VALUE && mailboxes.some((mailbox) => mailbox.id === selected)
      ? selected
      : NEW_MAILBOX_VALUE;
    setSelected(retainedSelection);
    setForm(withFreshRows(mailboxFormValue(
      draft,
      domain,
      retainedSelection === NEW_MAILBOX_VALUE ? null : retainedSelection
    )));
    setBaseRevision(draft.revision);
    setStatus('idle');
    setMessage('');
  }, [draft.revision]);

  function withFreshRows(value: MailboxFormValue): MailboxFormValue {
    return {
      ...value,
      alternatives: value.alternatives.map((row) => ({
        id: `alternative-${alternativeSequence.current++}`,
        value: row.value
      }))
    };
  }

  function markDirty() {
    setDirty(true);
    setRetrySetup(false);
    setStatus('idle');
    setMessage('');
  }

  function updateField<Key extends keyof MailboxFormValue>(key: Key, value: MailboxFormValue[Key]) {
    setForm((current) => ({ ...current, [key]: value }));
    markDirty();
  }

  function updateAlternative(rowId: string, value: string) {
    setForm((current) => ({
      ...current,
      alternatives: current.alternatives.map((row) => row.id === rowId ? { ...row, value } : row)
    }));
    markDirty();
  }

  function addAlternative() {
    setForm((current) => ({
      ...current,
      alternatives: [...current.alternatives, { id: `alternative-${alternativeSequence.current++}`, value: '' }]
    }));
    markDirty();
  }

  function removeAlternative(rowId: string) {
    setForm((current) => ({
      ...current,
      alternatives: current.alternatives.filter((row) => row.id !== rowId)
    }));
    markDirty();
  }

  function loadSelection(value: string) {
    if (!switchAllowed) return;
    const mailboxId = value === NEW_MAILBOX_VALUE ? null : value;
    setSelected(value);
    setForm(withFreshRows(mailboxFormValue(draft, domain, mailboxId)));
    setBaseRevision(draft.revision);
    setDirty(false);
    setRetrySetup(false);
    setStatus('idle');
    setMessage('');
  }

  function discardChanges() {
    const retainedSelection = selected !== NEW_MAILBOX_VALUE && mailboxes.some((mailbox) => mailbox.id === selected)
      ? selected
      : NEW_MAILBOX_VALUE;
    setSelected(retainedSelection);
    setForm(withFreshRows(mailboxFormValue(
      draft,
      domain,
      retainedSelection === NEW_MAILBOX_VALUE ? null : retainedSelection
    )));
    setBaseRevision(draft.revision);
    setDirty(false);
    setRetrySetup(false);
    setStatus('success');
    setMessage('Changes discarded.');
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saveDisabled) return;

    let input: MailboxProfileInput;
    try {
      input = buildMailboxProfileInput(form, domain);
    } catch (error: unknown) {
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Check the mailbox details and try again.');
      return;
    }

    setBusy(true);
    setStatus('saving');
    setMessage(retrySetup ? 'Retrying mailbox setup...' : 'Saving mailbox draft...');

    try {
      if (selectedMailboxId) {
        const next = await updateMailRouteDraftMailboxProfile({
          domain,
          mailboxId: selectedMailboxId,
          ...input,
          expectedRevision: baseRevision
        });
        if (!acceptDraft(next)) return;
        setDirty(false);
        setRetrySetup(false);
        setStatus('success');
        setMessage('Mailbox draft saved. Apply the draft when you are ready for mail delivery to change.');
        return;
      }

      const result = await setupMailboxPrincipal({
        ...input,
        expectedRevision: baseRevision
      });
      if (!acceptDraft(result.draft)) return;

      if (result.status === 'principal_pending') {
        setDirty(false);
        setRetrySetup(true);
        setStatus('warning');
        setMessage('Mailbox draft saved. Login setup is still pending. Retry the same setup when the directory is available.');
      } else if (result.status === 'principal_conflict') {
        setDirty(false);
        setRetrySetup(true);
        setStatus('error');
        setMessage('Mailbox draft saved, but login setup needs attention. Resolve the existing account and retry.');
      } else {
        const savedMailbox = result.draft.mailboxes.find((mailbox) =>
          mailbox.primaryEmail?.trim().toLowerCase() === input.primaryAddress
        );
        if (savedMailbox) setSelected(savedMailbox.id);
        setDirty(false);
        setRetrySetup(false);
        setStatus('success');
        setMessage('Mailbox draft saved. Apply the draft when you are ready for mail delivery to change.');
      }
    } catch (error: unknown) {
      setStatus('error');
      setMessage(saveErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  function acceptDraft(next: MailRouteDraft): boolean {
    if (!onDraftChange(next)) {
      setDirty(true);
      setRetrySetup(false);
      setStatus('error');
      setMessage('The draft changed while saving. Your input is preserved; discard it before saving again.');
      return false;
    }
    setBaseRevision(next.revision);
    return true;
  }

  return (
    <section className="route-draft-editor compact mailbox-editor" aria-label={`Create or edit a mailbox in ${domain}`}>
      <div className="mailbox-editor-heading">
        <div>
          <p className="section-label">Mailbox draft</p>
          <h4>{creating ? `Create a mailbox in ${domain}` : `Edit a mailbox in ${domain}`}</h4>
          <p>Save a draft first. Mail delivery changes only after you apply it.</p>
        </div>
      </div>

      <label className="mailbox-editor-selection" htmlFor="mailbox-editor-selection">
        <span>Mailbox</span>
        <select
          aria-label="Mailbox"
          disabled={!switchAllowed}
          id="mailbox-editor-selection"
          onChange={(event) => loadSelection(event.currentTarget.value)}
          value={selected}
        >
          <option value={NEW_MAILBOX_VALUE}>New mailbox</option>
          {mailboxes.map((mailbox, index) => (
            <option key={mailbox.id} value={mailbox.id}>{mailboxOptionLabel(mailbox, draft, index)}</option>
          ))}
        </select>
      </label>

      <form className="mailbox-editor-form" noValidate onSubmit={save}>
        <div className="mailbox-editor-fields">
          <label>
            <span>Email</span>
            <input
              aria-describedby="mailbox-editor-email-help"
              aria-invalid={missingPrimary || undefined}
              aria-required="true"
              autoCapitalize="none"
              autoComplete="email"
              onChange={(event) => creating && updateField('primaryAddress', event.currentTarget.value)}
              placeholder={`name@${domain}`}
              readOnly={!creating}
              spellCheck={false}
              type="email"
              value={form.primaryAddress}
              disabled={formDisabled}
            />
            <small id="mailbox-editor-email-help">
              {creating ? 'This address is the mailbox login.' : missingPrimary ? 'No primary email is configured for this mailbox.' : 'The primary email is the mailbox login and cannot be renamed here.'}
            </small>
          </label>

          <label>
            <span>Name</span>
            <input
              aria-required="true"
              autoComplete="name"
              onChange={(event) => updateField('displayName', event.currentTarget.value)}
              placeholder="Alex Example"
              type="text"
              value={form.displayName}
              disabled={formDisabled}
            />
          </label>

          <label className="mailbox-editor-field-wide">
            <span>Description <small>(optional)</small></span>
            <textarea
              onChange={(event) => updateField('description', event.currentTarget.value)}
              placeholder="A short note about this mailbox"
              rows={3}
              value={form.description}
              disabled={formDisabled}
            />
          </label>
        </div>

        <fieldset className="mailbox-editor-alternatives">
          <legend>Alternative emails</legend>
          <p className="mailbox-editor-help">These addresses receive mail and cannot be used to sign in.</p>
          <div className="mailbox-editor-alternative-list">
            {form.alternatives.map((row, index) => (
              <div className="mailbox-editor-alternative-row" key={row.id}>
                <label htmlFor={`mailbox-alternative-${row.id}`}>
                  <span>Alternative email {index + 1}</span>
                  <input
                    aria-label={`Alternative email ${index + 1}`}
                    autoCapitalize="none"
                    autoComplete="off"
                    id={`mailbox-alternative-${row.id}`}
                    onChange={(event) => updateAlternative(row.id, event.currentTarget.value)}
                    placeholder={`alias@${domain}`}
                    spellCheck={false}
                    type="email"
                    value={row.value}
                    disabled={formDisabled}
                  />
                </label>
                <button
                  aria-label={`Remove alternative email ${index + 1}`}
                  className="mailbox-editor-secondary-button"
                  disabled={formDisabled}
                  onClick={() => removeAlternative(row.id)}
                  type="button"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          <button className="mailbox-editor-secondary-button" disabled={formDisabled} onClick={addAlternative} type="button">Add alternative email</button>
        </fieldset>

        <label className="mailbox-editor-catch-all">
          <input
            checked={form.catchAll}
            onChange={(event) => updateField('catchAll', event.currentTarget.checked)}
            type="checkbox"
            disabled={formDisabled}
          />
          <span>Catch-all: deliver unmatched addresses to this mailbox</span>
        </label>

        {!creating && missingPrimary ? (
          <p className="mailbox-editor-feedback warning" role="status">This mailbox has no primary email, so saving is disabled until a login address is assigned.</p>
        ) : null}
        {!creating && primaryDomainMismatch ? (
          <p className="mailbox-editor-feedback warning" role="status">This mailbox's login address belongs to another domain, so profile changes are disabled here. Select a mailbox whose login belongs to {domain}.</p>
        ) : null}
        {stale ? (
          <p className="mailbox-editor-feedback warning" role="alert">A newer mailbox draft is loaded. Your input is preserved; discard it before saving.</p>
        ) : null}
        {message && !stale ? (
          <p aria-live="polite" className={`mailbox-editor-feedback ${status}`} role={status === 'error' ? 'alert' : 'status'}>{message}</p>
        ) : null}

        <div className="mailbox-editor-actions">
          <button disabled={saveDisabled} type="submit">
            {busy ? (retrySetup ? 'Retrying...' : creating ? 'Creating...' : 'Saving...') : retrySetup ? 'Retry setup' : creating ? 'Create mailbox' : 'Save changes'}
          </button>
          {(dirty || stale || retrySetup) ? <button className="mailbox-editor-secondary-button" disabled={busy} onClick={discardChanges} type="button">Discard changes</button> : null}
        </div>
      </form>
    </section>
  );
}

function saveErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 409) {
    return 'The draft changed in another session. Your input is preserved; reload the latest draft before trying again.';
  }
  if (error instanceof ApiError && error.status === 400) {
    return 'The mailbox details were rejected. Check the email addresses and try again.';
  }
  if (error instanceof Error && error.message) return error.message;
  return 'The mailbox draft was not saved. Your input is preserved; try again.';
}
